import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CANCEL_AGENT_EVAL_RUN,
  CREATE_AGENT_EVAL_TEST,
  DELETE_AGENT_EVAL_TEST,
  DOWNLOAD_AGENT_EVAL_REPORT,
  GENERATE_AGENT_EVAL_TESTS,
  GET_AGENT_EVAL_RUN,
  GET_AGENT_EVAL_RUN_STATUS,
  GET_AGENT_EVAL_SETTINGS,
  LIST_AGENT_EVAL_RUNS,
  LIST_AGENT_EVAL_TESTS,
  PREVIEW_AGENT_EVAL_SAMPLE,
  PROJECT_API_KEY,
  START_AGENT_EVAL_RUN,
  UPDATE_AGENT_EVAL_SETTINGS,
  UPDATE_AGENT_EVAL_TEST,
} from 'src/@core/services/util/api.endpoints';
import { AgentEvalRun, AgentEvalSamplePreview, AgentEvalTest } from './agent-evals.types';
import { AgentEvalSuccessRules } from './success-rules-dialog/success-rules-dialog.component';

@Injectable({ providedIn: 'root' })
export class AgentEvalsService {
  constructor(private http: HttpClient) {}

  private headers(): HttpHeaders {
    return new HttpHeaders({
      'x-api-key': PROJECT_API_KEY,
      'Content-Type': 'application/json',
    });
  }

  listTests(entityId: string, projectId: string): Observable<AgentEvalTest[]> {
    const params = new HttpParams()
      .set('entity_id', entityId)
      .set('project_id', projectId);
    return this.http
      .get<any>(LIST_AGENT_EVAL_TESTS, { headers: this.headers(), params })
      .pipe(map((res) => (res && res.data) || []));
  }

  createTest(payload: Partial<AgentEvalTest> & { entity_id: string; project_id: string; name: string }): Observable<AgentEvalTest> {
    return this.http
      .post<any>(CREATE_AGENT_EVAL_TEST, payload, { headers: this.headers() })
      .pipe(map((res) => (res && res.data) || res));
  }

  updateTest(payload: any): Observable<AgentEvalTest> {
    return this.http
      .post<any>(UPDATE_AGENT_EVAL_TEST, payload, { headers: this.headers() })
      .pipe(map((res) => (res && res.data) || res));
  }

  deleteTest(entityId: string, projectId: string, testId: string): Observable<any> {
    return this.http.post(
      DELETE_AGENT_EVAL_TEST,
      { entity_id: entityId, project_id: projectId, test_id: testId },
      { headers: this.headers() }
    );
  }

  generateTests(entityId: string, projectId: string, persist: boolean): Observable<AgentEvalTest[]> {
    return this.http
      .post<any>(
        GENERATE_AGENT_EVAL_TESTS,
        { entity_id: entityId, project_id: projectId, persist },
        { headers: this.headers() }
      )
      .pipe(map((res) => (res && res.data) || []));
  }

  previewSample(
    entityId: string,
    projectId: string,
    limits?: { lt_60s?: number; m1_to_2?: number; gt_2m?: number; all?: number },
    humanReviewedOnly?: boolean
  ): Observable<AgentEvalSamplePreview> {
    let params = new HttpParams()
      .set('entity_id', entityId)
      .set('project_id', projectId);
    if (limits) {
      Object.keys(limits).forEach((key) => {
        const val = (limits as any)[key];
        if (val !== undefined && val !== null) {
          params = params.set(key, String(val));
        }
      });
    }
    if (humanReviewedOnly) {
      params = params.set('human_reviewed_only', '1');
    }
    return this.http.get<AgentEvalSamplePreview>(PREVIEW_AGENT_EVAL_SAMPLE, {
      headers: this.headers(),
      params,
    });
  }

  startRun(payload: {
    entity_id: string;
    project_id: string;
    mode?: string;
    limits?: any;
    test_ids?: string[];
    human_reviewed_only?: boolean;
  }): Observable<any> {
    return this.http.post(START_AGENT_EVAL_RUN, payload, { headers: this.headers() });
  }

  listRuns(entityId: string, projectId: string, limit = 50): Observable<AgentEvalRun[]> {
    const params = new HttpParams()
      .set('entity_id', entityId)
      .set('project_id', projectId)
      .set('limit', String(limit));
    return this.http
      .get<any>(LIST_AGENT_EVAL_RUNS, { headers: this.headers(), params })
      .pipe(map((res) => (res && res.data) || []));
  }

  getRunStatus(entityId: string, projectId: string, runId: string): Observable<AgentEvalRun> {
    const params = new HttpParams()
      .set('entity_id', entityId)
      .set('project_id', projectId)
      .set('run_id', runId);
    return this.http
      .get<any>(GET_AGENT_EVAL_RUN_STATUS, { headers: this.headers(), params })
      .pipe(map((res) => (res && res.data) || res));
  }

  getRun(entityId: string, projectId: string, runId: string): Observable<AgentEvalRun> {
    const params = new HttpParams()
      .set('entity_id', entityId)
      .set('project_id', projectId)
      .set('run_id', runId);
    return this.http
      .get<any>(GET_AGENT_EVAL_RUN, { headers: this.headers(), params })
      .pipe(map((res) => (res && res.data) || res));
  }

  /** Short API path that 302s to an attachment-disposition S3 URL. */
  downloadReportUrl(entityId: string, projectId: string, runId: string): string {
    const params = new HttpParams()
      .set('entity_id', entityId)
      .set('project_id', projectId)
      .set('run_id', runId);
    return `${DOWNLOAD_AGENT_EVAL_REPORT}?${params.toString()}`;
  }

  cancelRun(entityId: string, projectId: string, runId: string): Observable<any> {
    return this.http.post(
      CANCEL_AGENT_EVAL_RUN,
      { entity_id: entityId, project_id: projectId, run_id: runId },
      { headers: this.headers() }
    );
  }

  getSettings(entityId: string, projectId: string): Observable<{ success_rules: any; updated_at?: string }> {
    const params = new HttpParams()
      .set('entity_id', entityId)
      .set('project_id', projectId);
    return this.http
      .get<any>(GET_AGENT_EVAL_SETTINGS, { headers: this.headers(), params })
      .pipe(map((res) => (res && res.data) || res));
  }

  updateSettings(
    entityId: string,
    projectId: string,
    rules: AgentEvalSuccessRules
  ): Observable<any> {
    return this.http
      .post<any>(
        UPDATE_AGENT_EVAL_SETTINGS,
        {
          entity_id: entityId,
          project_id: projectId,
          success_rules: {
            pass_mark: rules.passMark,
            alert_below: rules.alertBelow,
            critical_double: rules.criticalDouble,
            notify_by: rules.notifyBy,
          },
        },
        { headers: this.headers() }
      )
      .pipe(map((res) => (res && res.data) || res));
  }
}
